import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { EmptyState, StatusBadge } from "@/components/app/ui-bits";
import { Plus, Trash, Notebook, ArrowUUpLeft, CheckCircle, Eye, Database, PlayCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import RecurringJournalsPanel from "@/pages/finance/RecurringJournalsPanel";

const EMPTY_LINE = { account_code: "", debit: "", credit: "", description: "" };

// ─────────────── Migration panel (admin) ───────────────
function MigrationPanel({ onDone }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/finance/gl/migration/status"); setStatus(data); }
    catch { /* non-blocking */ }
  };
  useEffect(() => { load(); }, []);

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/gl/migration/backfill", { dry_run: dryRun });
      setReport(data);
      toast.success(dryRun
        ? `Dry-run complete · ${data.totals.entries_created} entries would be created`
        : `Migration posted · ${data.totals.entries_created} journal entries (batch ${data.batch_no})`);
      load();
      if (!dryRun) onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="dc-card p-4 space-y-3" data-testid="gl-migration-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="overline flex items-center gap-1"><Database size={12} /> Shadow-ledger back-fill</div>
          <div className="text-xs text-slate-500 mt-1">
            Converts existing subledgers (entries · AR · AP · payroll · petty cash · assets) into posted double-entry journals. Idempotent — re-runs skip anything already migrated.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => run(true)} disabled={busy} data-testid="migration-dryrun-btn">
            <Eye size={12} className="mr-1" /> Dry run
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" className="btn-primary-blue" disabled={busy} data-testid="migration-run-btn">
                <PlayCircle size={12} className="mr-1" /> Run migration
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Post the back-fill journals?</AlertDialogTitle></AlertDialogHeader>
              <div className="text-sm text-slate-600">This writes posted journal entries into the GL. Existing subledger screens keep working — the GL runs alongside them. Re-running is safe (idempotent).</div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => run(false)} data-testid="migration-confirm-btn">Run back-fill</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      {status?.migrated_entries > 0 && !report && (
        <div className="text-xs text-slate-600" data-testid="migration-status-line">
          <b>{status.migrated_entries.toLocaleString()}</b> migrated journal entries in the GL
          {status.last_run ? ` · last run ${formatDate(status.last_run.run_at)} by ${status.last_run.run_by}` : ""}.
        </div>
      )}
      {report && (
        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="migration-report">
          <div className="text-xs font-semibold text-slate-800">
            {report.dry_run ? "DRY-RUN — nothing written" : `POSTED · batch ${report.batch_no}`} ·{" "}
            {report.totals.entries_created.toLocaleString()} entries · {report.totals.lines_created.toLocaleString()} lines ·{" "}
            DR {formatCurrency(report.totals.total_debit)} = CR {formatCurrency(report.totals.total_credit)} ·{" "}
            <span className={report.totals.balanced ? "text-emerald-600" : "text-rose-600"}>
              {report.totals.balanced ? "BALANCED ✓" : "NOT BALANCED"}
            </span>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500"><th>Source</th><th className="text-right">Scanned</th><th className="text-right">Journaled</th><th className="text-right">Skipped</th><th className="text-right">Source $</th></tr></thead>
            <tbody>
              {Object.entries(report.sources).map(([s, v]) => (
                <tr key={s} className="border-t border-slate-200">
                  <td className="py-1 font-mono-alt">{s}</td>
                  <td className="text-right tabular-nums">{v.scanned}</td>
                  <td className="text-right tabular-nums text-emerald-700">{v.journaled}</td>
                  <td className="text-right tabular-nums text-slate-500" title={Object.entries(v.skip_reasons || {}).map(([k, n]) => `${k}: ${n}`).join(", ")}>{v.skipped}</td>
                  <td className="text-right tabular-nums">{formatCurrency(v.source_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────── New journal dialog ───────────────
function JournalDialog({ open, onOpenChange, accounts, onSaved }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [memo, setMemo] = useState("");
  const [lines, setLines] = useState([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setDate(new Date().toISOString().slice(0, 10)); setMemo(""); setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]); }
  }, [open]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const totals = useMemo(() => {
    const dr = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const cr = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    return { dr: Math.round(dr * 100) / 100, cr: Math.round(cr * 100) / 100 };
  }, [lines]);
  const balanced = Math.abs(totals.dr - totals.cr) < 0.005 && totals.dr > 0;

  const save = async (postNow) => {
    if (!memo.trim()) { toast.error("Memo is required"); return; }
    setSaving(true);
    try {
      const payload = {
        date, memo,
        post_immediately: postNow,
        lines: lines.filter((l) => l.account_code).map((l) => ({
          account_code: l.account_code,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          description: l.description || "",
        })),
      };
      const { data } = await api.post("/finance/gl/journals", payload);
      toast.success(`${data.entry_no} ${postNow ? "posted" : "saved as draft"}`);
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>New journal entry</DialogTitle>
          <DialogDescription>Debits must equal credits before this entry can be saved.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="journal-form-date" />
          </div>
          <div className="col-span-2">
            <Label>Memo</Label>
            <Input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="What is this entry for?" data-testid="journal-form-memo" />
          </div>
        </div>
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_110px_1fr_32px] items-center gap-2" data-testid={`journal-line-${i}`}>
              <Select value={l.account_code || undefined} onValueChange={(v) => setLine(i, "account_code", v)}>
                <SelectTrigger className="h-9" data-testid={`journal-line-account-${i}`}><SelectValue placeholder="Account…" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" min="0" step="0.01" placeholder="Debit" value={l.debit}
                onChange={(e) => setLine(i, "debit", e.target.value)} data-testid={`journal-line-debit-${i}`} />
              <Input type="number" min="0" step="0.01" placeholder="Credit" value={l.credit}
                onChange={(e) => setLine(i, "credit", e.target.value)} data-testid={`journal-line-credit-${i}`} />
              <Input placeholder="Line description" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} />
              <Button variant="outline" size="sm" className="h-9 px-2" disabled={lines.length <= 2}
                onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}>
                <Trash size={12} />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, { ...EMPTY_LINE }])} data-testid="journal-add-line">
            <Plus size={12} className="mr-1" /> Add line
          </Button>
          <div className="text-sm font-mono-alt tabular-nums" data-testid="journal-balance-indicator">
            DR <b>{formatCurrency(totals.dr)}</b> · CR <b>{formatCurrency(totals.cr)}</b> ·{" "}
            <span className={balanced ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>
              {balanced ? "Balanced ✓" : `Off by ${formatCurrency(Math.abs(totals.dr - totals.cr))}`}
            </span>
          </div>
        </div>
        <DialogFooter>
          <div className="mr-auto text-[11px] text-amber-700">Maker-checker: a second finance user must post this journal.</div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-primary-blue" onClick={() => save(false)} disabled={saving || !balanced} data-testid="journal-save-draft">Save draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Journal detail (lines) dialog ───────────────
function JournalDetail({ entryId, onClose }) {
  const [entry, setEntry] = useState(null);
  useEffect(() => {
    if (!entryId) return;
    api.get(`/finance/gl/journals/${entryId}`).then(({ data }) => setEntry(data)).catch((e) => toast.error(formatApiError(e)));
  }, [entryId]);
  return (
    <Dialog open={!!entryId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{entry ? `${entry.entry_no} · ${entry.memo}` : "Loading…"}</DialogTitle>
          <DialogDescription>
            {entry ? `${formatDate(entry.date)} · period ${entry.period} · ${entry.source_module} · ${entry.status.toUpperCase()}` : ""}
          </DialogDescription>
        </DialogHeader>
        {entry && (
          <div className="responsive-table-wrap" data-testid="journal-detail-lines">
            <table className="data-table w-full">
              <thead><tr><th>#</th><th>Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th>Description</th></tr></thead>
              <tbody>
                {(entry.lines || []).map((l) => (
                  <tr key={l.id}>
                    <td className="text-xs tabular-nums">{l.line_no}</td>
                    <td className="text-xs"><span className="font-mono-alt">{l.account_code}</span> · {l.account_name}</td>
                    <td className="text-right font-mono-alt tabular-nums">{l.debit ? formatCurrency(l.debit) : "—"}</td>
                    <td className="text-right font-mono-alt tabular-nums">{l.credit ? formatCurrency(l.credit) : "—"}</td>
                    <td className="text-xs text-slate-500">{l.description || "—"}</td>
                  </tr>
                ))}
                <tr className="font-semibold border-t-2 border-slate-300">
                  <td /><td>Totals</td>
                  <td className="text-right font-mono-alt tabular-nums">{formatCurrency(entry.total_debit)}</td>
                  <td className="text-right font-mono-alt tabular-nums">{formatCurrency(entry.total_credit)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {entry?.reverses_entry_id && <div className="text-xs text-amber-700">This entry reverses another journal (linked).</div>}
        {entry?.reversed_by_entry_id && <div className="text-xs text-amber-700">This entry has been reversed.</div>}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Main tab ───────────────
export default function GLJournalsTab({ canEdit, isAdmin, userEmail }) {
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewId, setViewId] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (sourceFilter !== "all") params.source_module = sourceFilter;
      const [{ data: j }, { data: a }] = await Promise.all([
        api.get("/finance/gl/journals", { params }),
        api.get("/finance/gl/accounts"),
      ]);
      setData(j);
      setAccounts((a.items || []).filter((x) => x.is_postable && x.active));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [statusFilter, sourceFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async (id) => {
    try { await api.post(`/finance/gl/journals/${id}/post`); toast.success("Journal posted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reverse = async (id) => {
    try { const { data: r } = await api.post(`/finance/gl/journals/${id}/reverse`, {}); toast.success(`Reversed via ${r.reversal_entry_no}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeDraft = async (id) => {
    try { await api.delete(`/finance/gl/journals/${id}`); toast.success("Draft deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>;
  const items = data.items || [];
  const sources = ["manual", "finance_entries", "ar", "ap", "payroll", "petty_cash", "assets"];

  return (
    <div className="space-y-4" data-testid="gl-journals-tab">
      {isAdmin && <MigrationPanel onDone={load} />}

      <div className="grid grid-cols-3 gap-3">
        {["draft", "posted", "reversed"].map((s) => (
          <div key={s} className="dc-card p-3" data-testid={`journal-count-${s}`}>
            <div className="overline">{s}</div>
            <div className="font-display text-xl font-bold text-slate-900 tabular-nums">{(data.counts?.[s] || 0).toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 h-8" data-testid="journal-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="posted">Posted</SelectItem>
              <SelectItem value="reversed">Reversed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-40 h-8" data-testid="journal-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => setDialogOpen(true)} data-testid="journal-new-btn">
            <Plus size={12} className="mr-1" /> New journal
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Notebook} title="No journal entries"
          description="Create a manual journal or run the shadow-ledger migration to back-fill from existing subledgers." />
      ) : (
        <div className="dc-card responsive-table-wrap">
          <table className="data-table w-full min-w-[1000px]">
            <thead>
              <tr>
                <th>Entry no</th><th>Date</th><th>Period</th><th>Memo</th><th>Source</th>
                <th className="text-right">Debit</th><th className="text-right">Credit</th><th>Status</th>
                {canEdit && <th className="w-32">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="cursor-pointer hover:bg-slate-50/70 transition" onClick={() => setViewId(e.id)} data-testid={`journal-row-${e.id}`}>
                  <td className="font-mono-alt text-xs tabular-nums">{e.entry_no}{e.is_reversal && <span className="ml-1 text-[9px] text-amber-600 uppercase">rev</span>}</td>
                  <td className="font-mono-alt text-xs tabular-nums">{formatDate(e.date)}</td>
                  <td className="font-mono-alt text-xs tabular-nums">{e.period}</td>
                  <td className="text-xs text-slate-800 max-w-[280px] truncate">{e.memo}</td>
                  <td className="text-xs uppercase tracking-wider text-slate-500">{e.source_module}</td>
                  <td className="text-right font-mono-alt tabular-nums">{formatCurrency(e.total_debit)}</td>
                  <td className="text-right font-mono-alt tabular-nums">{formatCurrency(e.total_credit)}</td>
                  <td><StatusBadge status={e.status} /></td>
                  {canEdit && (
                    <td onClick={(ev) => ev.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {e.status === "draft" && (
                          <>
                            <Button variant="outline" size="sm" className="h-7" title={e.created_by === userEmail ? "Maker-checker: prepared by you — a second finance user must post it" : "Post"}
                              disabled={e.created_by === userEmail}
                              onClick={() => post(e.id)} data-testid={`journal-post-${e.id}`}>
                              <CheckCircle size={12} />
                            </Button>
                            <Button variant="outline" size="sm" className="h-7" title="Delete draft" onClick={() => removeDraft(e.id)} data-testid={`journal-delete-${e.id}`}>
                              <Trash size={12} />
                            </Button>
                          </>
                        )}
                        {e.status === "posted" && !e.reversed_by_entry_id && (
                          <Button variant="outline" size="sm" className="h-7" title="Reverse" onClick={() => reverse(e.id)} data-testid={`journal-reverse-${e.id}`}>
                            <ArrowUUpLeft size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecurringJournalsPanel canEdit={canEdit} accounts={accounts} onGenerated={load} />

      <JournalDialog open={dialogOpen} onOpenChange={setDialogOpen} accounts={accounts} onSaved={load} />
      <JournalDetail entryId={viewId} onClose={() => setViewId(null)} />
    </div>
  );
}
